import { useCallback, useEffect, useRef, useState } from "react";
import {
  getDatasetEpisodeReview,
  getReviewOptions,
  type ReviewOptions,
  type EpisodeSummary,
} from "@/api";
import { episodeReviewSavedStore } from "../episodeReviewSavedStore";
import { useDatasetEditLock } from "./useDatasetEditLock";

// 저장 확인 플래시가 목록 행에 남는 시간. 사용자가 알아볼 만큼 길고, 다음 에피소드를 판정하는
// 흐름을 방해하지 않을 만큼 짧다.
const SAVED_FLASH_MS = 1_500;

/**
 * 공용 Dataset 상세의 에피소드 annotation 소스.
 *
 * 선택 에피소드의 annotation 상태(+`annotation_version`)는 FileBrowser가 지연 로드한
 * `EpisodeSummary`에 이미 포함되어 있다. 태그 어휘·코멘트 상한만 옵션 계약에서 받고, 저장 응답과
 * 409 단건 재조회 결과는 해당 에피소드의 로컬 override로 유지한다. 초기 리뷰 데이터는 에피소드
 * 목록 응답만 사용한다.
 *
 * StrictMode dispose-reuse 프리즈를 피하려고 Lifecycle 클래스 없이 plain 훅으로 둔다(cancelled
 * 플래그만). `enabled=false` 면 fetch 하지 않는다.
 */
export type DatasetEpisodeAnnotations = {
  options: ReviewOptions;
  loading: boolean;
  // FileBrowser가 선택한 행에 저장·충돌 재조회 결과가 있으면 덮어쓴다.
  rowForEpisode: (episode: EpisodeSummary) => EpisodeSummary;
  // 저장 응답을 해당 에피소드의 로컬 override로 보존한다.
  applySaved: (row: EpisodeSummary) => void;
  // 방금 저장한 에피소드. 목록이 그 행에 짧은 확인 표시를 띄운다. 값은 곧 null 로 돌아온다.
  savedFlashUuid: string | null;
  // 409 후 그 에피소드만 단건 조회해 같은 override에 반영한다.
  reload: (episodeUuid: string) => Promise<void>;
  // 학습이 이 Dataset 을 쓰는 중이다(조회 시점의 힌트). 리뷰 독은 이 값으로 안내만 하고 막지 않는다.
  // 리뷰 독은 에피소드마다 다시 마운트되므로 여기서 데이터셋마다 한 번만 조회한다.
  trainingLocked: boolean;
};

const DEFAULT_OPTIONS: ReviewOptions = {
  episode_quality_tags: [],
  review_comment_max_length: 2000,
};

export function useDatasetEpisodeAnnotations(
  datasetUuid: string,
  enabled: boolean,
): DatasetEpisodeAnnotations {
  const [savedRows, setSavedRows] = useState<readonly EpisodeSummary[]>([]);
  const [options, setOptions] = useState<ReviewOptions>(DEFAULT_OPTIONS);
  const [loading, setLoading] = useState(enabled);
  const [savedFlashUuid, setSavedFlashUuid] = useState<string | null>(null);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const trainingLocked = useDatasetEditLock(datasetUuid, enabled);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    let cancelled = false;
    setSavedRows((current) => (current.length === 0 ? current : []));
    setLoading(true);
    void getReviewOptions().then((opts) => {
      if (cancelled) {
        return;
      }
      setOptions(opts);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [datasetUuid, enabled]);

  const rowForEpisode = useCallback(
    (episode: EpisodeSummary): EpisodeSummary =>
      savedRows.find((row) => row.uuid === episode.uuid) ?? episode,
    [savedRows],
  );

  /**
   * 저장 성공. override 를 갱신하고, 그 행에 확인 플래시를 설정한다.
   *
   * 저장 확인은 toast 가 아니라 이 플래시다. 리뷰에서는 Enter·S 를 연속으로 누르므로 toast 를
   * 띄우면 화면이 알림으로 덮인다. 대신 방금 저장한 행이 잠깐 밝아진다. 선택은 이미 다음
   * 에피소드로 이동했으므로, 사용자는 행 위치로 "무엇이 저장됐는지" 알 수 있다.
   */
  const applySaved = useCallback(
    (row: EpisodeSummary): void => {
      setSavedRows((prev) => upsertRow(prev, row));
      // 필터 축의 개수와 목록 구성이 방금 달라졌을 수 있다. 모든 저장 성공은 이 경로 하나를 거친다.
      // 그래서 어느 축을 고쳐도 이 한 줄로 레인과 목록에 알린다. 개수는 레인이 팝오버를 열 때, 목록은
      // 서버 필터가 걸려 있을 때 다시 받는다. 진단 표면은 이 에피소드의 품질 후보 확인을 다시 받는다.
      episodeReviewSavedStore.markSaved(datasetUuid, row.uuid);
      // 연속 저장이면 이전 타이머를 버리고 다시 시작한다. 그래서 새 행의 표시가 이전 타이머 때문에
      // 일찍 사라지지 않는다.
      if (flashTimerRef.current !== null) {
        clearTimeout(flashTimerRef.current);
      }
      setSavedFlashUuid(row.uuid);
      flashTimerRef.current = setTimeout(() => {
        flashTimerRef.current = null;
        setSavedFlashUuid(null);
      }, SAVED_FLASH_MS);
    },
    [datasetUuid],
  );

  useEffect(() => {
    return () => {
      if (flashTimerRef.current !== null) {
        clearTimeout(flashTimerRef.current);
      }
    };
  }, []);

  const reload = useCallback(
    async (episodeUuid: string): Promise<void> => {
      const row = await getDatasetEpisodeReview(datasetUuid, episodeUuid).catch(() => null);
      if (row === null) {
        return;
      }
      setSavedRows((prev) => upsertRow(prev, row));
    },
    [datasetUuid],
  );

  return { options, loading, rowForEpisode, applySaved, savedFlashUuid, reload, trainingLocked };
}

function upsertRow(
  rows: readonly EpisodeSummary[],
  row: EpisodeSummary,
): readonly EpisodeSummary[] {
  return rows.some((current) => current.uuid === row.uuid)
    ? rows.map((current) => (current.uuid === row.uuid ? row : current))
    : [...rows, row];
}
